import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { TFactoryState } from '@monorepo/types';

type TAnalyticsConfigCacheEntry = {
  factoryState: TFactoryState['factoryInfo'];
  timestamp: number;
};

const CACHE_KEYS = {
  ANALYTICS: 'oi_analytics_config',
  MM_ANALYTICS: 'oi_mm_analytics_config',
  SKILLI8_ANALYTICS: 'oi_skilli8_analytics_config',
} as const;

const CACHE_TTL_MS = 2 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class AnalyticsConfigCacheService {
  #storage: Storage | null = null;

  constructor(private storage: Storage) {}

  async #getStorage(): Promise<Storage> {
    if (!this.#storage) this.#storage = await this.storage.create();
    return this.#storage;
  }

  #getCacheKey(isMmAnalytics: boolean, isSkilli8Analytics: boolean = false): string {
    if (isSkilli8Analytics) return CACHE_KEYS.SKILLI8_ANALYTICS;
    if (isMmAnalytics) return CACHE_KEYS.MM_ANALYTICS;
    return CACHE_KEYS.ANALYTICS;
  }

  async getCachedConfig(
    isMmAnalytics: boolean,
    isSkilli8Analytics: boolean = false,
  ): Promise<TFactoryState['factoryInfo'] | null> {
    try {
      const store = await this.#getStorage();
      const key = this.#getCacheKey(isMmAnalytics, isSkilli8Analytics);
      const entry: TAnalyticsConfigCacheEntry | null = await store.get(key);

      if (!entry) return null;

      const isExpired = Date.now() - entry.timestamp > CACHE_TTL_MS;
      if (isExpired) return null;

      return entry.factoryState;
    } catch (e) {
      console.error('[AnalyticsConfigCache] Error reading cache', e);
      return null;
    }
  }

  async cacheConfig(
    isMmAnalytics: boolean,
    factoryState: TFactoryState['factoryInfo'],
    isSkilli8Analytics: boolean = false,
  ): Promise<void> {
    try {
      const store = await this.#getStorage();
      const key = this.#getCacheKey(isMmAnalytics, isSkilli8Analytics);

      const entry: TAnalyticsConfigCacheEntry = {
        factoryState,
        timestamp: Date.now(),
      };

      await store.set(key, entry);
    } catch (e) {
      console.error('[AnalyticsConfigCache] Error writing cache', e);
    }
  }

  async clearCache(): Promise<void> {
    try {
      const store = await this.#getStorage();
      await store.remove(CACHE_KEYS.ANALYTICS);
      await store.remove(CACHE_KEYS.MM_ANALYTICS);
      await store.remove(CACHE_KEYS.SKILLI8_ANALYTICS);
    } catch (e) {
      console.error('[AnalyticsConfigCache] Error clearing cache', e);
    }
  }
}
